import React, {useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import Cookies from 'js-cookie';
import apiService from '../services/apiService';
import {prysmDB} from '../utils/database';
import {getVisitorUUID} from '../utils/uuidManager';
import {COOKIE_NAME} from '../config';
import clientLogger from '../utils/clientLogger';

const ThankYou = () => {
    const navigate = useNavigate();

    useEffect(() => {
        const sendRegistrationNotice = async () => {
            try {
                let jwt = window.getAuthToken ? window.getAuthToken() : Cookies.get(COOKIE_NAME);
                if (!jwt) {
                    // Try to reset session and get new token
                    if (window.resetSession) {
                        await window.resetSession();
                        jwt = window.getAuthToken ? window.getAuthToken() : Cookies.get(COOKIE_NAME);
                        if (!jwt) {
                            console.error('No JWT token found and unable to create new session');
                            return;
                        }
                    } else {
                        console.error('No JWT token found');
                        return;
                    }
                }

                const vuuid = getVisitorUUID();
                if (!vuuid) {
                    console.error('No visitor UUID found');
                    return;
                }

                // Get registration response from local storage
                let registrationResponse = null;
                try {
                    registrationResponse = await prysmDB.get('registration_data', 'registration_response');
                    console.log('[ThankYou] Loaded registration response from storage:', registrationResponse);
                } catch (error) {
                    console.warn('[ThankYou] Could not load registration response from storage:', error);
                }

                const response = await apiService.sendRegistrationNotice(registrationResponse);

                if (!response.ok) {
                    console.error('Failed to send registration notice');
                }
            } catch (error) {
                console.error('Error sending registration notice:', error);
            }
        };

        const clearAllSessionData = async () => {
            try {
                console.log('[ThankYou] Starting complete session cleanup...');

                // Get current visitor UUID for logging before clearing
                const currentUUID = getVisitorUUID();

                // Clear all localStorage data using the database utility
                await prysmDB.deleteDatabase();
                console.log('[ThankYou] Database cleared successfully');

                // Clear additional localStorage items that might not be caught by deleteDatabase
                const additionalKeys = [
                    'adobeCleanFontAdded',
                    'clientLogger_failedLogs',
                    'square-debug'
                ];

                additionalKeys.forEach(key => {
                    localStorage.removeItem(key);
                    console.log(`[ThankYou] Removed additional localStorage key: ${key}`);
                });

                // Clear all sessionStorage data
                sessionStorage.clear();
                console.log('[ThankYou] SessionStorage cleared successfully');

                // Clear authentication cookies
                Cookies.remove(COOKIE_NAME);
                console.log('[ThankYou] Authentication cookies cleared');

                // Clear any remaining localStorage items that might contain our data
                const allLocalStorageKeys = Object.keys(localStorage);
                allLocalStorageKeys.forEach(key => {
                    // Clear any keys that might be related to our application
                    if (key.includes('prysm') || key.includes('registration') || key.includes('square') ||
                        key.includes('selfie') || key.includes('license') || key.includes('sms') ||
                        key.includes('visitor') || key.includes('clientLogger')) {
                        localStorage.removeItem(key);
                        console.log(`[ThankYou] Cleared additional localStorage key: ${key}`);
                    }
                });

                // Call server to reset session if UUID is available
                if (currentUUID) {
                    try {
                        await apiService.resetSession();
                        console.log('[ThankYou] Server session reset completed');
                    } catch (serverError) {
                        console.error('[ThankYou] Server reset failed, but local reset completed:', serverError);

                        // Log API error to clientPrysm logger (this might fail since we're clearing data)
                        try {
                            clientLogger.logApiError('/api/session/reset', serverError, {
                                action: 'reset_session_from_thank_you',
                                method: 'POST',
                                visitorUUID: currentUUID
                            });
                        } catch (logError) {
                            console.warn('[ThankYou] Could not log server reset error:', logError);
                        }
                    }
                }

                // Dispatch events to notify components of reset
                window.dispatchEvent(new CustomEvent('sessionReset'));
                window.dispatchEvent(new CustomEvent('uuidUpdated'));

                console.log('[ThankYou] Complete session cleanup finished successfully');
            } catch (error) {
                console.error('[ThankYou] Error during session cleanup:', error);

                // Log the cleanup error
                try {
                    clientLogger.logError('thank_you_session_cleanup_error', `Failed to complete session cleanup: ${error.message}`, error, {
                        context: 'thank_you_page_cleanup'
                    });
                } catch (logError) {
                    console.warn('[ThankYou] Could not log cleanup error:', logError);
                }
            }
        };

        const completeRegistrationProcess = async () => {
            try {
                // First, send the registration notice
                await sendRegistrationNotice();

                // Then, clear all session data
                await clearAllSessionData();
            } catch (error) {
                console.error('[ThankYou] Error in complete registration process:', error);
            }
        };

        // Execute the complete registration process
        completeRegistrationProcess();
    }, []);

    return (
        <div
            className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 flex items-center justify-center p-4 relative overflow-hidden">
            {/* Decorative background elements */}
            <div className="absolute inset-0 overflow-hidden">
                <div
                    className="absolute -top-40 -right-40 w-80 h-80 bg-purple-900 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
                <div
                    className="absolute -bottom-40 -left-40 w-80 h-80 bg-indigo-900 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
                <div
                    className="absolute top-40 left-40 w-80 h-80 bg-gray-800 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
            </div>

            <div className="w-full max-w-2xl relative z-10">
                <div
                    className="bg-gray-800/80 backdrop-blur-lg rounded-2xl shadow-2xl p-8 transform transition-all duration-500 hover:scale-[1.02] border border-gray-700">
                    <div className="text-center">
                        <h1 className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-purple-500 mb-4 animate-fade-in">
                            Thank You!
                        </h1>
                        <div
                            className="h-1 w-20 bg-gradient-to-r from-pink-500 to-purple-600 mx-auto rounded-full animate-glow mb-8"></div>

                        <p className="text-xl text-gray-300 mb-8">
                            Our membership team will review and process your information. Once it is complete they will
                            contact you if they have any questions.
                            Once approved you will be emailed additional information. If you do not hear from us within
                            24 hours, please contact us
                            suupport@ourprysm.com. Again, thank you for becoming part of the Prsym family.
                        </p>

                        <button
                            onClick={() => window.location.href = 'https://ourprysm.com'}
                            className="px-6 py-3 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-xl hover:from-pink-600 hover:to-purple-700 transform transition-all duration-200 hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 shadow-md"
                        >
                            Return to Home
                        </button>
                    </div>
                </div>
            </div>

            <style jsx="true">{`
        @keyframes blob {
          0% {
            transform: translate(0px, 0px) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
          100% {
            transform: translate(0px, 0px) scale(1);
          }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
        @keyframes glow {
          0% {
            box-shadow: 0 0 5px rgba(147, 51, 234, 0.2), 0 0 10px rgba(147, 51, 234, 0.2);
          }
          50% {
            box-shadow: 0 0 20px rgba(147, 51, 234, 0.4), 0 0 30px rgba(147, 51, 234, 0.4);
          }
          100% {
            box-shadow: 0 0 5px rgba(147, 51, 234, 0.2), 0 0 10px rgba(147, 51, 234, 0.2);
          }
        }
        .animate-glow {
          animation: glow 2s infinite;
        }
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in {
          animation: fade-in 1s ease-out;
        }
      `}</style>
        </div>
    );
};

export default ThankYou;
